import {
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Box,
  ModalCloseButton,
} from '@chakra-ui/react';
import { compositionGroupApi } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { compositionGroupEvents } from '@texas/components/shared/compositionGroup/events';
import { CompositionGroupView } from '../../compositions/shared/group/CompositionGroupView';
import { ConfirmCopy } from '../../compositions/shared/group/ConfirmCopy';
import { CopyProps, GroupedVersions } from '../shared/GroupedVersions';
import { useVersionOverview } from '../shared/useVersionOverview';
import { OptionsOverview } from './Options';
import {
  Branch,
  VariantBranchSupplier,
} from '@texas/api/endpoints/variantsApi';
import { useActiveContext } from '@texas/hooks/useActiveContext';
import { MultiSelectContext } from '../Versions';
import { SelectedVersionsMenu } from '../shared/SelectedVersionsMenu';
import { ArchiveCompositionGroupModal } from '../shared/ArchiveCompositionGroupModal';
import { useVersionOverviewContext } from '../shared/VersionOverviewContext';
import { getRowId } from '@texas/components/shared/samples/helper';

interface Props {
  variantId: number;
  suppliers: VariantBranchSupplier[];
  branches: Branch[];
}

export function VersionsListViewStandard({
  variantId,
  suppliers,
  branches,
}: Props) {
  const { t } = useTranslation();

  const { versions, copyGroup, versionsLoading } = useVersionOverview({
    variantId,
    isMatrix: false,
  });

  const {
    modalComponents,
    selectedRows,
    menuItems,
    filterByVersions,
    setSelectedRows,
  } = useActiveContext(MultiSelectContext);

  const { filters } = useVersionOverviewContext();

  useEffect(() => {
    setSelectedRows({});
  }, [filters, setSelectedRows]);

  useEffect(() => {
    filterByVersions(
      versions?.map((d) => ({
        groupId: d.id,
        groupArchived: !!d.archived,
        variantId: d.variantId,
        supplierId: d.supplierId!,
        articleFolderId: d.folderId,
        branchId: d.branchId!,
        sample: d.mainProductApproval?.mainSample
          ? {
              sampleReadyDate: d.mainProductApproval.mainSample.sampleReadyDate,
              productApproval: d.mainProductApproval,
              sampleId: d.mainProductApproval.mainSample.id,
              receivedFromSupplierUnknownShipment:
                d.mainProductApproval.mainSample
                  .receivedFromSupplierUnknownShipment,
              state: d.mainProductApproval.mainSample.state,
              transport: d.mainProductApproval.mainSample.transport,
              lifecycle: d.mainProductApproval.mainSample.lifecycle,
            }
          : null,
      })) ?? [],
    );
  }, [filterByVersions, versions]);

  const {
    value: copyValue,
    isOpen: isCopyOpen,
    onClose: onCopyClose,
    onOpen: onCopyOpen,
  } = useValueDisclosure<CopyProps>();
  const { value, isOpen, onClose, onOpen } = useValueDisclosure<{
    id: number;
    readOnly: boolean;
  }>();
  const {
    value: archiveValue,
    isOpen: isArchiveOpen,
    onClose: onArchiveClose,
    onOpen: onArchiveOpen,
  } = useValueDisclosure<number>();
  const {
    value: restoreValue,
    isOpen: isRestoreOpen,
    onClose: onRestoreClose,
    onOpen: onRestoreOpen,
  } = useValueDisclosure<number>();

  return (
    <Box>
      <OptionsOverview
        suppliers={suppliers}
        branches={branches}
        variantId={variantId}
      />
      <SelectedVersionsMenu
        menuItems={menuItems}
        selectedRowsCount={
          Object.keys(selectedRows).filter((x) => selectedRows[x]).length
        }
        onClear={() => setSelectedRows({})}
        onSelectAll={() => {
          if (!versions) return;
          const v = Object.fromEntries(
            versions.map((v) => [getRowId(v.id), true]),
          );
          setSelectedRows(v);
        }}
      />
      <GroupedVersions
        header={null}
        versions={versions ?? []}
        onRestoreOpen={(id) => onRestoreOpen(id)}
        onArchiveOpen={(id) => onArchiveOpen(id)}
        onOpen={(id: number, readOnly: boolean) => onOpen({ id, readOnly })}
        onCopyOpen={(data) => onCopyOpen(data)}
        versionsLoading={versionsLoading}
      />

      {modalComponents}
      {archiveValue && (
        <ArchiveCompositionGroupModal
          groupId={archiveValue}
          isOpen={isArchiveOpen}
          onClose={onArchiveClose}
        />
      )}
      {restoreValue && (
        <VerifyDialogWithRequest
          headerTitle={t('general.restore')}
          secondaryButtonTitle={t('general.cancel')}
          primaryButtonTitle={t('general.confirm')}
          primaryRequest={compositionGroupApi.restore}
          onPerformed={() => compositionGroupEvents.onRestored.dispatch()}
          args={[restoreValue]}
          isOpen={isRestoreOpen}
          onClose={onRestoreClose}
          onSuccessTitle={t('general.restored')}
        >
          {t('alert.areYouSure')}
        </VerifyDialogWithRequest>
      )}
      {copyValue && (
        <ConfirmCopy
          title={
            copyValue.replaceId
              ? t('compositionGroup.version.resetToOption')
              : t('compositionGroup.version.copyIntoNew')
          }
          buttonLabel={
            copyValue.replaceId
              ? t('compositionGroup.version.resetToOption')
              : t('compositionGroup.version.copyIntoNew')
          }
          isOpen={isCopyOpen}
          onClose={onCopyClose}
          compositionGroupId={copyValue.groupId}
          hideCopyConfig={true}
          onConfirm={async (id) => {
            onCopyClose();
            await copyGroup({
              fromId: id,
              basedOnId: copyValue.basedOnId ?? undefined,
              location: {
                variantId: variantId,
                supplierId: copyValue.supplierId,
                branchId: copyValue.branchId,
                cellX: copyValue.cellX ?? undefined,
                cellY: copyValue.cellY ?? undefined,
              },
              replaceId: copyValue.replaceId ?? undefined,
            });
          }}
        />
      )}
      <Modal isCentered={true} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent width="auto" maxW="fit-content">
          <ModalCloseButton />
          <ModalBody>
            {value && <InnerModal id={value.id} readOnly={value.readOnly} />}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function InnerModal({ id, readOnly }: { id: number; readOnly: boolean }) {
  return (
    <>
      <CompositionGroupView
        variantId={0}
        compositionGroupId={id}
        componentSettings={{
          expandedDefault: true,
          compositionArchiveRestore: 'hidden',
          addMaterials: 'hidden',
          materialsMode: 'only-edit',
          removeMaterials: 'hidden',
          dimensionMode: 'only-values',
          readOnly: readOnly,
        }}
      />
    </>
  );
}
